import { i18n } from 'i18n';
import {
  BackfillRecipeForm,
  BackfillWarningDto,
  FillType,
  PourPresetDto,
  RheologyDataDto,
  ThroughputControlType
} from 'providers/api';
import { round } from 'utils';
import { pumpPressureKpaToBar } from 'utils/unitConversions';
import { Model } from '../hooks/useModelState';
import { PCStope } from '../MineModel/Stopes';
import PCRoute from '../MineModel/Route/PCRoute';
import { generateAllRoutes, routeOptsForProject, returnShortestRoute } from '../MineModel/Route/utils';
import { calculateRecipeFromStrength, getUCSTargetDays } from '../canvas/PCFunctions/PCPasteFunctions';
import { updateRecipeState } from '../canvas/Model/updateRecipeState';
import { calculateFrictionForRoute } from '../canvas/Model/updateFriction';
import { makeDictionaryLimits } from '../StopePanelPage/RecipesTab/RecipeForm/RecipeFormHelpers';
import { OptimisationErrorType, PasteRecipe, RecipeState } from '../MineModel/types';

export interface AutoRecipeOptimisationResult {
  form: BackfillRecipeForm;
  failed: boolean;
}

// Mirror of the private cloneRoute in RecipeForm.tsx — keeps the live route untouched while the
// solver mutates its working copy.
const cloneRoute = (sourceRoute: PCRoute): PCRoute => {
  const cloned = Object.create(Object.getPrototypeOf(sourceRoute));
  Object.assign(cloned, sourceRoute);
  cloned.routePoints = sourceRoute.routePoints.map((rp) => ({ ...rp }));
  return cloned;
};

const ucsBelowTargetWarning = (): BackfillWarningDto => ({
  code: 'FormUcsStrengthBelowTarget',
  display: i18n._('UCS STRENGTH BELOW TARGET'),
});

const makeSpec = (recipe: PasteRecipe) => ({
  massConcentration: round(recipe.concentrationByMass * 100),
  binderContent: round(recipe.cement * 100),
  ucsStrength: recipe.actualUCSKpa,
  pumpPressure: pumpPressureKpaToBar(recipe.actualPumpPressureKpa),
});

/**
 * Headless paste-recipe optimisation for the auto-pour flow. Runs the same solver the interactive
 * RecipeForm uses (generateAllRoutes -> returnShortestRoute -> calculateRecipeFromStrength) and
 * assembles a fully-populated BackfillRecipeForm, so auto-created recipes carry real spec values
 * instead of zeros.
 *
 * Returns null for unrecoverable preconditions (hydraulic fill, reticulation not loaded, no route).
 * On optimiser failure (e.g. UCS unreachable) returns { failed: true } with a best-effort form plus
 * a warning so the caller still creates the recipe + pour.
 */
export const optimiseAutoRecipeForm = (
  model: Model,
  stope: PCStope,
  preset: PourPresetDto,
  rheologyDataset: RheologyDataDto,
): AutoRecipeOptimisationResult | null => {
  // Hydraulic is handled by the caller's existing builder for now.
  if (model.project.fillType === FillType.Hydraulic) return null;

  if (!model.activeBackfillPlant || !model.spools || !model.pipes || !model.nodes || !stope?.originNode) {
    return null;
  }

  let liveRoute: PCRoute | undefined;
  try {
    const { routeObjects } = generateAllRoutes(
      model.activeBackfillPlant,
      model.spools,
      stope,
      Object.values(model.pipes),
      routeOptsForProject(model.project),
      model.nodes,
      model.pipes,
    );
    liveRoute = returnShortestRoute(routeObjects);
  } catch {
    return null;
  }
  if (!liveRoute) return null;

  const limits = makeDictionaryLimits(rheologyDataset);
  const throughputControlType = model.project.throughput;
  const targetUCSStrength = preset.ucsStrength ?? 0;
  const targetDays = preset.curingDays || getUCSTargetDays(rheologyDataset.ucsCoefficients);
  const pumpPressure = limits.pumpPressure.default ?? 0;
  const throughput = limits.tons.default || limits.tons.min;

  const commonForm = {
    reference: preset.name,
    rheologyDataSetId: rheologyDataset.rheologyDataSetId,
    backfillPlantId: model.activeBackfillPlant?.id ?? '',
    pourPresetId: preset.entityId,
    stopeId: stope.data.stopeId,
    isAutoRecipe: true,
    spools: liveRoute.routePointStrings ?? [],
    routeSpools: liveRoute.spoolIds,
    pasteSpecification: {
      pumpPressure,
      targetDays,
      targetUCSStrength,
      throughput,
      throughputControlType,
    },
  };

  const prevRecipeState = model.recipeState;
  try {
    const result = calculateRecipeFromStrength(
      model,
      rheologyDataset,
      targetUCSStrength,
      targetDays,
      pumpPressure,
      throughput,
      cloneRoute(liveRoute),
      throughputControlType,
      undefined,
    );

    const {
      cement, concentrationByMass, actualUCSKpa, actualPumpPressureKpa,
    } = result.optimisedResult;
    const binderContent = round(cement * 100);
    const massConcentration = round(concentrationByMass * 100);
    const pumpPressureBar = pumpPressureKpaToBar(actualPumpPressureKpa);

    if (result.optimisedResult.error !== OptimisationErrorType.NONE) {
      const spec = {
        massConcentration, binderContent, ucsStrength: actualUCSKpa, pumpPressure: pumpPressureBar,
      };
      const form: BackfillRecipeForm = {
        ...commonForm,
        pasteOptimisedSpecification: spec,
        pasteSelectedSpecification: spec,
        warnings: [ucsBelowTargetWarning()],
      };
      return { form, failed: true };
    }

    const initialRecipeState: RecipeState = {
      totalDryConcentrationByMass: concentrationByMass,
      binderConcentrationByMass: cement,
      tailingsSolidsDensity: rheologyDataset.tailingsDrySolidsDensity,
      binderParticleDensity: rheologyDataset.binderDryParticleDensity,
      carrierFluidDensity: rheologyDataset.carrierFluidDensity,
      throughputControlType,
      tailingsDryTonnage: throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined,
      wetTonnage: throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined,
      wetFlowRate: throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined,
      rheologyDataSetId: rheologyDataset.rheologyDataSetId,
    };

    const optimisedRecipeState = updateRecipeState(
      model,
      initialRecipeState,
      concentrationByMass,
      cement,
      rheologyDataset.tailingsDrySolidsDensity,
      rheologyDataset.binderDryParticleDensity,
      rheologyDataset.carrierFluidDensity,
      throughputControlType === ThroughputControlType.DryTonnage ? throughput : undefined,
      throughputControlType === ThroughputControlType.FlowRate ? throughput : undefined,
      throughputControlType === ThroughputControlType.WetTonnage ? throughput : undefined,
    );
    model.setRecipeState(optimisedRecipeState);

    const recalculatedRoute = calculateFrictionForRoute(model, optimisedRecipeState, cloneRoute(liveRoute));
    const finalPumpPressureBar = recalculatedRoute?.pumpPressureKpa
      ? pumpPressureKpaToBar(recalculatedRoute.pumpPressureKpa)
      : pumpPressureBar;

    const selectedSpec = {
      massConcentration, binderContent, ucsStrength: actualUCSKpa, pumpPressure: finalPumpPressureBar,
    };

    const routeWarnings: BackfillWarningDto[] = recalculatedRoute?.warnings ?? [];
    const warnings = targetUCSStrength > actualUCSKpa
      ? [...routeWarnings, ucsBelowTargetWarning()]
      : routeWarnings;

    const form: BackfillRecipeForm = {
      ...commonForm,
      pasteOptimisedSpecification: selectedSpec,
      pasteSelectedSpecification: selectedSpec,
      pasteSlackFlowSpecification: result.slackFlowResult ? makeSpec(result.slackFlowResult) : undefined,
      pasteMaxPressureSpecification: result.maxPressureResult ? makeSpec(result.maxPressureResult) : undefined,
      warnings,
    };

    return { form, failed: false };
  } finally {
    model.setRecipeState(prevRecipeState);
  }
};
